/**
 * Utilitários para processamento de conteúdo de posts
 */

import MarkdownIt from 'markdown-it';

const md = new MarkdownIt({
  html: false,
  breaks: true,
  linkify: true,
  typographer: true,
});

// Habilitar tabelas (plugin built-in do markdown-it)
md.enable('table');

/**
 * Processa conteúdo markdown para exibição no frontend
 * Remove formatação markdown e retorna texto limpo
 * @param {string} content - Conteúdo markdown
 * @returns {string} - Texto processado
 */
export function processContent(content) {
  if (!content) return '';

  // Remove cabeçalhos markdown (# ## ###)
  let processed = content.replace(/^#{1,6}\s+/gm, '');

  // Remove formatação em negrito (**texto** ou __texto__)
  processed = processed.replace(/\*\*(.*?)\*\*/g, '$1');
  processed = processed.replace(/__(.*?)__/g, '$1');

  // Remove formatação em itálico (*texto* ou _texto_)
  processed = processed.replace(/\*(.*?)\*/g, '$1');
  processed = processed.replace(/_(.*?)_/g, '$1');

  // Remove links markdown [texto](url)
  processed = processed.replace(/\[([^\]]+)\]\([^)]+\)/g, '$1');

  // Remove imagens ![alt](url)
  processed = processed.replace(/!\[([^\]]*)\]\([^)]+\)/g, '');

  // Remove listas (- item ou * item ou números)
  processed = processed.replace(/^[\s]*[-*+]\s+/gm, '');
  processed = processed.replace(/^[\s]*\d+\.\s+/gm, '');

  // Remove quebras de linha excessivas
  processed = processed.replace(/\n{3,}/g, '\n\n');

  // Remove espaços em branco no início e fim
  processed = processed.trim();

  return processed;
}

/**
 * Cria uma prévia do conteúdo para exibição em cards/listas
 * @param {string} content - Conteúdo completo
 * @param {number} maxLength - Comprimento máximo da prévia
 * @returns {string} - Prévia do conteúdo
 */
export function createContentPreview(content, maxLength = 150) {
  const processed = processContent(content);

  if (processed.length <= maxLength) {
    return processed;
  }

  // Corta no último espaço antes do limite
  const truncated = processed.substring(0, maxLength);
  const lastSpaceIndex = truncated.lastIndexOf(' ');

  if (lastSpaceIndex > 0) {
    return truncated.substring(0, lastSpaceIndex) + '...';
  }

  return truncated + '...';
}

/**
 * Unescape de sequências JSON residuais dos posts gerados por IA
 */
function unescapeJsonContent(content) {
  let text = content;
  try {
    text = text.replace(/\\\\/g, '\\');
    text = text.replace(/\\n/g, '\n');
    text = text.replace(/\\t/g, '\t');
    text = text.replace(/\\r/g, '\r');
    text = text.replace(/\\"/g, '"');
    text = text.replace(/\\'/g, "'");
  } catch (e) {
    console.warn('Erro ao fazer unescape, usando abordagem simples:', e.message);
    text = text.replace(/\\n/g, '\n');
  }
  return text;
}

/**
 * Processa conteúdo markdown para HTML usando markdown-it
 * Suporte completo: headers, bold, italic, listas, links, imagens,
 * blockquotes, tabelas, inline code, code blocks, strikethrough, hr
 * @param {string} content - Conteúdo markdown
 * @returns {string} - HTML processado
 */
export function markdownToHtml(content) {
  if (!content) return '';

  // Unescape de sequências JSON residuais (compatibilidade com posts IA)
  const cleaned = unescapeJsonContent(content);

  // Renderizar markdown para HTML usando markdown-it
  let html = md.render(cleaned);

  // Adicionar classes Tailwind aos elementos gerados
  html = html.replace(/<h1>/g, '<h1 class="text-3xl font-bold mt-6 mb-4">');
  html = html.replace(/<h2>/g, '<h2 class="text-2xl font-semibold mt-6 mb-4">');
  html = html.replace(/<h3>/g, '<h3 class="text-xl font-semibold mt-6 mb-3">');
  html = html.replace(/<h4>/g, '<h4 class="text-lg font-semibold mt-4 mb-2">');
  html = html.replace(/<p>/g, '<p class="mb-4">');
  html = html.replace(/<ul>/g, '<ul class="list-disc list-inside mb-4">');
  html = html.replace(/<ol>/g, '<ol class="list-decimal list-inside mb-4">');
  html = html.replace(/<li>/g, '<li class="ml-4 mb-1">');
  html = html.replace(/<blockquote>/g, '<blockquote class="border-l-4 border-blue-500 pl-4 my-4 italic text-gray-600 bg-gray-50 p-4 rounded-r-lg">');
  // Envolver tabelas num container scrollável para mobile
  html = html.replace(/<table>/g, '<div class="table-responsive-wrapper"><table>');
  html = html.replace(/<\/table>/g, '</table></div>');
  html = html.replace(/<thead>/g, '<thead class="bg-gray-50">');
  html = html.replace(/<th([^>]*)>/g, '<th$1 class="border border-gray-200 px-4 py-3 bg-gray-50 font-semibold text-gray-700">');
  html = html.replace(/<td([^>]*)>/g, '<td$1 class="border border-gray-200 px-4 py-3 text-gray-600">');
  html = html.replace(/<tr>/g, '<tr class="even:bg-gray-50/50">');
  html = html.replace(/<hr>/g, '<hr class="my-8 border-gray-200">');
  html = html.replace(/<hr\/>/g, '<hr class="my-8 border-gray-200"/>');
  html = html.replace(/<a /g, '<a target="_blank" rel="noopener noreferrer" class="text-blue-600 hover:text-blue-800 underline" ');
  html = html.replace(/<img /g, '<img class="rounded-lg max-w-full h-auto my-4" ');
  html = html.replace(/<strong>/g, '<strong class="font-semibold">');

  return html;
}
